/**
 * Comparação de conteúdo entre duas versões do webchat.
 *
 * Existe para responder uma pergunta só: **mudou algo desde a última publicação?** Sem ela,
 * `publishWebchat` fazia `version + 1` sem olhar o conteúdo, e cada clique em Publicar criava uma
 * versão nova idêntica à anterior. O histórico enche de entradas iguais e deixa de servir para o que
 * foi feito — achar o ponto em que algo mudou para poder voltar.
 *
 * Mora no engine porque as duas pontas precisam do **mesmo veredito**: o servidor, para não gravar, e
 * o painel, para desabilitar o botão. Duas implementações divergiriam, e o sintoma seria um botão
 * habilitado que não faz nada — mesma razão de `matchesDomain` e `collectPublishBlockers` viverem aqui.
 */
/**
 * JSON com as chaves de objeto ordenadas, recursivamente.
 *
 * Ordenar não é preciosismo: o draft chega do navegador com a ordem que o painel montou, enquanto o
 * `published` foi gravado a partir da saída do zod, cuja ordem segue a declaração do schema. Comparar
 * `JSON.stringify` direto acusaria diferença em todo clique, e a checagem não serviria para nada.
 *
 * Ordem de **array** continua significativa: reordenar blocos muda o documento salvo, e tratar isso
 * como igual seria esconder uma alteração real.
 */
export function canonicalJson(value) {
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value) ?? 'null';
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(',')}]`;
    const entradas = Object.entries(value)
        // `undefined` desaparece no JSON de qualquer lado, então descartar aqui mantém os dois iguais
        // quando um lado traz a chave explicitamente indefinida e o outro simplesmente não a traz.
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entradas.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
}
/**
 * `true` quando aparência e fluxo são o mesmo conteúdo.
 *
 * Compara só esses dois campos: `version` e `publishedAt` são metadados que mudam a cada publicação e
 * fariam qualquer par parecer diferente.
 */
export function sameVersionContent(a, b) {
    if (!a || !b)
        return false;
    return (canonicalJson(a.appearance) === canonicalJson(b.appearance) &&
        canonicalJson(a.flow) === canonicalJson(b.flow));
}
//# sourceMappingURL=canonical.js.map